// The commands a device takes (ZENITH.md §11.2): what each one does, said
// before it is sent, and the sentence the page shows once the cell has
// queued it. Used by the act panel for the commands that confirm first and
// by the table for the one that does not.
import { api, idempotencyKey } from "../../api/client";
import { oneConsole } from "../../api/oneConsole";
import type { useMutation } from "../../api/mutation";
import type { components } from "../../api/schema";
import { unrecognized } from "../../api/openEnum";

export type Device = components["schemas"]["Device"];
export type CommandName = components["schemas"]["DeviceCommandRequest"]["command"];
export type Action = CommandName | "poe_cycle" | "firmware_request";

/** What the page says after a command: the sentence and — for a gesture that
 * half succeeded (#639) — the one act that finishes it. */
export interface Notice { text: string; action?: { label: string; run(): void } }

/** Each act's words. `button` names the act on its panel's own button ("Reboot W003-Garage"); `menu`
 * is its entry in a device's Actions menu and a row's three-dot menu (#1889), where an entry that
 * opens a panel rather than acting at once ends in an ellipsis. */
export const ACTIONS: Record<Action, { button: string; menu: string; title: string; explain: string; danger?: boolean; confirm: boolean }> = {
  // What a reboot does, and no promise of how long it takes. No decision words this sentence. It
  // ended "usually within two minutes" until #1678: no document under docs/evidence records how
  // long a reboot takes (ruled 2026-10-13, note `4005630701` item 1, in the words below).
  reboot: { button: "Reboot", menu: "Reboot", title: "Reboot", explain: "The device restarts now and its clients drop until it is back.", confirm: true },
  blink: { button: "Blink", menu: "Blink LEDs", title: "Blink", explain: "The LEDs flash for half a minute so the device can be found.", confirm: false },
  // The switch's words. TODO(copy): "It does not come back without someone on site" reads against
  // the notice sendCommand shows after the send ("It comes back on the redirector with defaults"),
  // against its own first half and against the contract's description of `factory`. What a switch
  // does after a reset has not been read from one: that is #1730's held half, which needs a unit a
  // person can reach at its console. **Both sentences are kept exactly as they are for a switch, by
  // ruling** (2026-10-13, note `4005630701` item 2 on #1678: "Keep both as they are; file an item to
  // measure it"), until that reading exists. An access point's words are FACTORY_AP below.
  factory: { button: "Factory reset", menu: "Factory reset…", title: "Reset to factory defaults", explain: "The device loses its configuration and keeps only the redirector to find its controller again. It does not come back without someone on site, so the serial is typed, not clicked.", danger: true, confirm: true },
  // Restates ADR-0413 decision 4 (`4a`), amending how ADR-0367 decisions 1a, 4b and 6 and ADR-0404
  // decision 1c are said — one consequence line before the click and nothing else: the device reboots
  // and its clients drop until it is back. It promises no duration and no success, names neither the
  // company nor the product (#1678), and the act is named Upgrade (ADR-0404 1c, #1816). The act
  // itself names the device in place of "The device", and says nothing under its button (#1839).
  firmware_request: { button: "Upgrade", menu: "Upgrade firmware…", title: "Upgrade", explain: "The device reboots, and its clients drop until it is back.", confirm: true },
  // Restates ADR-0251 decision 2, ADR-0252 decisions 2, 4a and 5a, ADR-0300 decisions 1d, 1b, 3b and 5 — the
  // cycle is typed at the switch's console and the access point's own state is not asked, so it must
  // not promise the access point comes back. The port comes from the access point's LLDP, failing
  // that from the address the switch learned on a port, and failing both a person may name it (the
  // panel says, beside the pick, that they see what it last reported and that a port the switch
  // reports another device on is refused). It must not claim to reach every access point that has
  // stopped answering: one gone fully silent cannot be found. The sentence's subject is the command
  // and not the product (ADR-0295 decision a). Shortened by #1778 to what a person reads before the
  // button; ADR-0300 decision 5 leaves the text to the build and fixes what it may claim.
  poe_cycle: { button: "Power-cycle PoE", menu: "Power cycle", title: "Power-cycle the PoE port", explain: "This command turns the power on this access point's switch port off and back on, so it restarts and its clients drop until it is back. The port is found from the switch's reports: the neighbor the access point announces, or where the switch learned its address. One that has gone fully silent cannot be found, so nothing is sent until you name its port yourself.", confirm: true },
};

/** An access point's factory-reset words, from the one reading taken (#1730, 2026-10-22,
 * `docs/evidence/2026-10-22-a-factory-reset-rap630c-on-rel-4-2-build-11-reconnected-to-its-cell-by-itself-and-owgw-re-sent-the-document-it-held-before-zenith-did-anything.md`):
 * a RAP630C on Shasta-AP-NOS Rel 4.2 build 11, sent `factory` with the redirector kept, restarted,
 * reconnected to its cell with nobody on site, and the cell sent it the document it held for it.
 * So the confirmation says it comes back by itself and runs what the controller holds, and the
 * notice says the same. Neither states how long that takes: one reading is not a value (§29).
 * The serial is still typed: the act is unchanged (#1730's Out), and the reason is now the outage
 * and the loss of what only the device held, not that nobody can bring it back. */
export const FACTORY_AP = {
  explain: "The access point loses its configuration and restarts, keeping only the redirector. It finds its controller again by itself and runs the configuration the controller holds for it; its clients drop until then. The serial is typed, not clicked.",
  sent: "It restarts on defaults, finds its controller through the redirector, and is sent the configuration the controller holds for it.",
};

/** What an act says before it is sent, for this device. Only the factory reset differs by class:
 * an access point's words are measured (FACTORY_AP), a switch's are the held ones (#1730). */
export const explainFor = (a: Action, d: Pick<Device, "category">): string =>
  a === "factory" && d.category === "ap" ? FACTORY_AP.explain : ACTIONS[a].explain;

/** The order the table offers them in. The PoE cycle is not among them: it is
 * an access point's alone, and offered by its own gate (`device.poe.cycle`).
 * The firmware act is an access point's alone too, and `DeviceActions` drops it
 * for a switch (ADR-0013). */
export const ACTION_ORDER: Exclude<Action, "poe_cycle">[] = ["reboot", "blink", "firmware_request", "factory"];

/** Whether the device's cell has said it does not have this device at all
 * (ADR-0040). Only a switch can be in this state: its site membership is
 * Zenith's own record, so it goes on existing here after the cell has lost it.
 *
 * **It is not `online === false`, and the UI must not draw it that way.** An
 * offline device is one the cell has and is not talking to, and an operator
 * waits for it; an absent one is never coming back and the move is to unclaim
 * it. Telling an operator to wait for the second is the defect #280 reported.
 * A device whose `presence` is missing is one the cell was not asked about or
 * did not answer for — not known, and drawn as neither. */
export const isAbsent = (d: Device) => d.presence === "absent";

/** The one sentence the pages use for why nothing can be sent to a device the
 * cell does not have, so the table's tooltip and the detail page agree. */
export const ABSENT_EXPLAIN =
  "The site holds this switch's membership and its controller does not have the device. " +
  "Nothing can be sent to it; the move is to unclaim it, which removes the membership " +
  "and nothing else.";

/** Why an absent switch is absent, in the operator's words: the cell lost it
 * (ABSENT_EXPLAIN), or it was claimed in reserve and the cell has never
 * reported it, or the cell reported its serial as an access point. The table's
 * tooltip and the detail page both take it from here. */
// Restates ADR-0482 decision 5a — a switch claimed before its controller had seen it reads
// absent until the controller first reports it as a switch, and becomes the ordinary claim then
// with no act; one the controller reports as an access point stays absent and is unclaimed.
export function absentExplain(d: Device): string {
  switch (d.presence_reason) {
    case "never_seen":
      return "This switch was claimed before its controller had seen it, and the controller has not reported it yet. " +
        "Nothing can be sent to it until it connects; it becomes an ordinary claimed switch then, with nothing to do.";
    case "class_mismatch":
      return "This switch was claimed before its controller had seen it, and the controller reported its serial as an access point, " +
        "so it was not taken as this switch. Nothing can be sent to it; the move is to unclaim it and claim it again.";
    default:
      return ABSENT_EXPLAIN;
  }
}

/** The one sentence the pages use for why an act that needs the device
 * connected cannot be sent to a device that is not online — the head's and the
 * row's acts, and the Console tab — so they agree with each other and with the
 * refusal the server would answer (`notLive`, `internal/api/handlers_commands.go`).
 *
 * **It reads `online_state` as served and decides nothing.** `online` is
 * `false` for both `offline` and `unknown`, and the caller gates on it, which is
 * correct: the server refuses both with a `409`. What differs is only the
 * reason, so only the words read `online_state` — and no state is derived here
 * from `last_contact_at` or anything else (§9.4). */
export function notLiveExplain(d: Device, act: "a command" | "a console" | "a firmware upgrade"): string {
  // Restates ADR-0219 decision 5 and decision 3 — a device the cell's gateway holds no
  // record of is refused for its own reason, `no_gateway_record`, and is not offline: waiting
  // does not bring it back and the repair is at the cell. Which cause applies (never
  // connected, or a claim that did not complete) is not known here, so it is not said.
  if (d.online_state === "unknown") return `The controller holds no record of ${d.name}, so ${act} cannot reach it. Waiting will not change that; the repair is at the controller.`;
  return `${d.name} is offline; ${act} needs it connected to its controller.`;
}

/** Canonical serial, the way the API compares one (§8.2). */
export const canonical = (s: string) => s.toLowerCase().replace(/[:.\-\s]/g, "");

type Lane = ReturnType<typeof useMutation>;

/** Sends a command on the page's write lane and says what the cell accepted;
 * nothing when it was refused — the lane holds the Problem then. */
export async function sendCommand(m: Lane, d: Device, name: CommandName, confirm?: string): Promise<Notice | undefined> {
  const r = await m.runWith(() => api.POST("/devices/{serial}/command", { params: { path: { serial: d.serial }, header: { "Idempotency-Key": idempotencyKey() } },
    body: { command: name, confirm: name === "factory" ? confirm : undefined } }));
  if (!r.ok || !r.data) return undefined;
  const queued = r.data.command_id ? ` as ${r.data.command_id}` : "";
  return { text: name === "blink" ? `${d.name} is blinking its LEDs; controller ${r.data.cell_id} queued it${queued}.`
    : name === "reboot" ? `Reboot sent to ${d.name}; controller ${r.data.cell_id} queued it${queued}.`
    : `Factory reset sent to ${d.name}; controller ${r.data.cell_id} queued it${queued}. ${d.category === "ap" ? FACTORY_AP.sent : "It comes back on the redirector with defaults."}` };
}

/** Power-cycles the PoE port of an access point (ADR-0251, ADR-0300): the
 * switch port is the server's to resolve and never the page's. `expected` is
 * the port the preview showed, sent back so the cut lands on it or is refused
 * with nothing sent (decision 5b); `named` is a port a person named where the
 * server found none (decision 1b), and is sent only after the preview showed
 * it. A refusal is a Problem on the lane, drawn by the panel in its own words;
 * an answer is said whatever its outcome, and a port that may still be off is
 * said as that, because `outcome` and not the status is what tells them apart. */
export async function sendPoeCycle(m: Lane, d: Device, opts: { expected?: { switch_serial: string; port: string }; named?: { switch_serial: string; port: string } } = {}): Promise<Notice | undefined> {
  const body = opts.expected || opts.named ? { expected_port: opts.expected, named_port: opts.named } : undefined;
  const r = await m.runWith(() => oneConsole(d.serial, "poe-cycle", () => api.POST("/devices/{serial}/poe-cycle", { params: { path: { serial: d.serial }, header: { "Idempotency-Key": idempotencyKey() } }, body })));
  if (!r.ok || !r.data) return undefined;
  const where = `port ${r.data.cli_port} of switch ${r.data.switch_serial}`;
  switch (r.data.outcome) {
    case "cycled": return { text: `PoE on ${where} was turned off and back on; ${d.name} is restarting. Its status shows when it is back.` };
    case "restored": return { text: `The first session ended after ${where} lost power; it was turned back on and the switch reads it on. ${d.name} is restarting.` };
    case "stranded": return { text: `${where} may still be off, and ${d.name} with it. ${r.data.detail}` };
    case "not_sent": return { text: `Nothing was cut on ${where}: ${r.data.detail}` };
    // Restates ADR-0511 decision 5a — an outcome this build does not know is not read as nothing cut:
    // the port may be off.
    default: return { text: `The answer for ${where} is one this page does not recognize (${unrecognized(r.data.outcome)}), so whether it is off is not known here. ${r.data.detail ?? ""}`.trim() };
  }
}

/** What a firmware request may carry beyond the entry: `firmware`, the build
 * the operator accepts where no qualification covers it, and `line`, the build
 * the operator accepts although it moves the device onto another vendor's
 * firmware line (ADR-0259 decision 2c, ADR-0367 decision 2.2a). **Two names for
 * two refusals, never merged**: each answers its own, and one token answering
 * both would accept a move no message named. */
export interface FirmwareRequestAcceptance {
  firmware?: string; line?: string;
  /** A forced delivery (ADR-0411 `9b`, `6a`): the refused delivery it
   * overrides, and the running firmware line accepted, exactly as the server
   * served it on the options entry (`running_line`, #1845) and never typed or
   * derived here (ADR-0413 `5d`). */
  overrides?: string; unknownSource?: string;
}

export type FirmwareDelivery = components["schemas"]["FirmwareDelivery"];

/** Approves a firmware delivery (ADR-0236, ADR-0367): the entry by its
 * `artifact` digest and nothing else, and the acceptances each refusal handed
 * back in its `predicted_firmware`, never parsed out of the sentence (#1051).
 * It returns the rollout's answer and the delivery the server sent with it, for
 * the device page's Firmware card to show at once (#1841); a deployment that
 * hands requests to no rollout answers `recorded`, with no delivery. */
export async function sendFirmwareRequest(m: Lane, d: Device, artifact: string, accept: FirmwareRequestAcceptance = {}): Promise<FirmwareAnswer | undefined> {
  const r = await m.runWith(() => api.POST("/devices/{serial}/firmware-requests", { params: { path: { serial: d.serial }, header: { "Idempotency-Key": idempotencyKey() } }, body: { artifact, accept_firmware: accept.firmware, accept_line_change: accept.line, overrides_delivery: accept.overrides, accept_unknown_source: accept.unknownSource } }));
  if (!r.ok || !r.data) return undefined;
  return { status: r.data.status, delivery: r.data.delivery };
}

/** The record-only answer's one line (ADR-0367 `4b`, ADR-0413 `8a`). */
export const RECORDED_NOT_SENT = "Recorded, not sent: this deployment passes no requests to the firmware rollout.";

/** What a firmware request answered: the rollout's answer, and the delivery the
 * server returned with `accepted` and `refused`. No notice is drawn for it
 * anywhere since #1841: the device page's Firmware card is the answer. */
/** `status` is the named list or any other string (ADR-0511); nothing reads it but a test. */
export interface FirmwareAnswer { status: string; delivery?: FirmwareDelivery }
